'use client';

import { useRouter } from 'next/navigation';

import { sendTrackingEvent } from '@/actions/tracking';
import { useCheckoutStore, useCustomerStore } from '@/stores';
import { ROUTES } from '@/types/enums';
import { offerPrice, type PromotionOffer } from '@/utils';

export type UpsellVariant = 'post_game' | 'pre_game';

type UsePostGameUpsellModalParams = {
  raffleId: string;
  raffleName: string;
  offer: PromotionOffer;
  ticketPrice: number;
  variant?: UpsellVariant;
  onClose: () => void;
  onDismiss?: () => void;
};

type UsePostGameUpsellModalReturn = {
  price: number;
  fullPrice: number;
  handleAccept: () => void;
  handleDecline: () => void;
};

export const usePostGameUpsellModal = ({
  raffleId,
  raffleName,
  offer,
  ticketPrice,
  variant = 'post_game',
  onClose,
  onDismiss,
}: UsePostGameUpsellModalParams): UsePostGameUpsellModalReturn => {
  const router = useRouter();
  const { resetCheckout, setPurchaseData, setUtmData, setAffiliateData, checkoutData } =
    useCheckoutStore();
  const { customerData } = useCustomerStore();
  const { utmData, affiliateData } = checkoutData;

  const price = offerPrice({
    quantity: offer.quantity,
    unitPrice: ticketPrice,
    percentage: offer.percentage,
  });
  const fullPrice = offerPrice({
    quantity: offer.quantity,
    unitPrice: ticketPrice,
    percentage: 0,
  });

  const handleAccept = () => {
    const upsellMetadata = {
      isUpsell: true,
      type: variant,
      step: variant === 'pre_game' ? 'before_game' : 'after_order',
      optionSelected: offer.quantity,
      originalQuantity: 0,
      newQuantity: offer.quantity,
    };

    const purchaseData = {
      raffleId,
      raffleName,
      ticketsAmount: offer.quantity,
      ticketPrice,
      totalValue: price,
      promotionOffers: [offer],
      promotionOffersResolved: true,
      acceptedOffers: [{ offerId: offer.id, ticketsAmount: offer.quantity }],
      isPostGameUpsell: true,
      upsell: upsellMetadata,
    };

    onClose();
    resetCheckout();

    if (utmData) setUtmData(utmData);
    if (affiliateData) setAffiliateData(affiliateData);

    setPurchaseData(purchaseData);

    sendTrackingEvent({
      event: 'SelectItem',
      customerId: customerData?.customerId,
      customer: {
        user: {
          name: customerData?.name,
          email: customerData?.email || '',
          phone: customerData?.phone,
        },
      },
      value: price,
      currency: 'BRL',
      utm: utmData
        ? {
            source: utmData.utm_source,
            medium: utmData.utm_medium,
            campaign: utmData.utm_campaign,
            term: utmData.utm_term,
            content: utmData.utm_content,
          }
        : undefined,
      upsell: upsellMetadata,
    });

    router.push(ROUTES.Enum['/checkout']);
  };

  const handleDecline = () => {
    onClose();
    onDismiss?.();
  };

  return { price, fullPrice, handleAccept, handleDecline };
};
